import { type FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { renderContigAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { type IContigState, shouldLoadContig } from "@/redux/modules/contig.ts";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type InputTableQuerySpec } from "../../graphql.js";
import { createTableSpecForContigParent } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { ENTITY_TYPE } from "../../settings/settings.js";
import GenericDataTableContainer from "../core/grid/GenericDataTableContainer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { ChildEntitiesButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";

interface IContigRelatedEntitiesViewOwnProps {
  subtitle: string;
  relatedEntitiesType: string;
  relatedEntitiesSubType?: number;
  middleToolbarElement?: React.ReactNode;
}

const ContigRelatedEntitiesView: FC<IContigRelatedEntitiesViewOwnProps> = (props) => {
  const [tableSpec, setTableSpec] = useState<InputTableQuerySpec>();
  const { contigId } = useParams<"contigId">();
  const viewId = useURLViewID();
  const { contig } = useSelector<IAppState, IContigState>((state) => state.contig);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadContig, contigId);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (contig && props.relatedEntitiesType) {
      setTableSpec(createTableSpecForContigParent(props.relatedEntitiesType, contig.id, props.relatedEntitiesSubType));
    }
  }, [contig, props.relatedEntitiesType, props.relatedEntitiesSubType]);

  return (
    <GenericDataTableContainer
      title={contig?.sequenceId ?? ""}
      subtitle={props.subtitle}
      tableSpec={tableSpec}
      middleToolbarElement={props.middleToolbarElement}
      rightToolbarElement={
        <ChildEntitiesButtonHeader
          tableSpec={tableSpec}
          getParentEntityName={() => contig?.sequenceId ?? ""}
          hideAnalyzeBtn={props.relatedEntitiesType === ENTITY_TYPE.MARKER}
        />
      }
      viewId={viewId ?? undefined}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderContigAttributesWidgets(contig)}
          key={"contig-related-entities-view-widget-1"}
        />,
      ]}
    />
  );
};

export default ContigRelatedEntitiesView;
